import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

/**
 * Opens the client in the provider's agent portal on the agent's own
 * computer, through the local REV&U downloader ("Find a client").
 *
 * The button is a revu:// link: the browser asks to open "REV&U Commissions",
 * the downloader shows a confirmation window and then logs in (if needed),
 * searches the client by ID and leaves the client's page open.
 *
 * Shown only for providers the downloader supports.
 */

// handler_code -> provider key in the downloader (links.py PROVIDER_NAMES).
// Only providers whose downloader module has open_client.
const PROVIDER_BY_HANDLER_CODE: Record<string, { key: string; name: string }> = {
  "512065202": { key: "meitav", name: "מיטב" },
  "513173393": { key: "altshuler", name: "אלטשולר" },
  "513611509": { key: "yelin", name: "ילין לפידות" },
  "514956465": { key: "more", name: "מור" },
  "511880460": { key: "analyst", name: "אנליסט" },
  "520023185": { key: "phoenix", name: "הפניקס" },
  "513026484": { key: "phoenix", name: "הפניקס" },
  "512244146": { key: "clal", name: "כלל" },
  "520024647": { key: "clal", name: "כלל" },
};

interface OpenClientInProviderButtonProps {
  handlerCode?: string;
  /** The client's ID number (product.client.client_id) */
  clientId?: string;
  /** Optional - opens this specific policy where the provider supports it */
  policyNumber?: string;
}

const OpenClientInProviderButton: React.FC<OpenClientInProviderButtonProps> = ({ handlerCode, clientId, policyNumber }) => {
  const provider = handlerCode ? PROVIDER_BY_HANDLER_CODE[handlerCode] : undefined;
  const id = (clientId ?? "").replace(/\D/g, "");

  if (!provider || !id) return null;

  // The downloader accepts digits, letters, "-" and "/" (3-30 chars); otherwise
  // the policy is not sent and the client is opened.
  const policy = (policyNumber ?? "").trim();
  const policyParam = /^[0-9A-Za-z][0-9A-Za-z\-/]{2,29}$/.test(policy)
    ? `&policy=${encodeURIComponent(policy)}`
    : "";

  const link = `revu://open-client?provider=${provider.key}&client=${encodeURIComponent(id)}${policyParam}`;

  return (
    <Tooltip title={`פתיחת הלקוח באתר ${provider.name} (דרך תוכנת REV&U במחשב)`}>
      <IconButton size="small" component="a" href={link}>
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default OpenClientInProviderButton;
